import { cn } from "../lib/utils";

/**
 * SectionToggle — a high-contrast labeled switch used inside the TaskModal
 * for "Schedule a time block" and "Add a location" rows.
 *
 * Designed to be unmistakable in both light AND dark themes:
 *  - OFF: filled outline track with a visible bordered thumb so it always
 *    reads against the surrounding card background.
 *  - ON:  glowing accent track + bright thumb + explicit ON/OFF mini-label.
 *  - 44px-wide tap target for mobile.
 */
export default function SectionToggle({
  icon,
  label,
  checked,
  onToggle,
  accent = "var(--primary)",
  testId,
  ariaLabel,
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-2 min-w-0">
        <span
          className="shrink-0 flex items-center justify-center w-7 h-7 rounded-md transition-colors"
          style={
            checked
              ? { backgroundColor: `${accent}1F`, color: accent }
              : { backgroundColor: "hsl(var(--secondary))", color: "hsl(var(--muted-foreground))" }
          }
        >
          {icon}
        </span>
        <span
          className={cn(
            "text-sm font-medium truncate transition-colors",
            checked ? "text-foreground" : "text-foreground/85"
          )}
        >
          {label}
        </span>
      </div>

      <div className="flex items-center gap-2 shrink-0">
        <span
          className={cn(
            "text-[10px] font-bold uppercase tracking-[0.15em] tabular-nums select-none transition-colors",
            checked ? "" : "text-muted-foreground"
          )}
          style={checked ? { color: accent } : undefined}
          aria-hidden="true"
        >
          {checked ? "On" : "Off"}
        </span>
        <button
          type="button"
          role="switch"
          aria-checked={checked}
          aria-label={ariaLabel || label}
          onClick={onToggle}
          onKeyDown={(e) => {
            if (e.key === " " || e.key === "Spacebar" || e.key === "Enter") {
              e.preventDefault();
              onToggle();
            }
          }}
          data-testid={testId}
          className={cn(
            "relative inline-flex items-center w-11 h-6 rounded-full transition-all duration-200",
            "ring-1 ring-inset focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-background",
            checked ? "ring-transparent" : "ring-border"
          )}
          style={{
            backgroundColor: checked ? accent : "hsl(var(--muted))",
            boxShadow: checked ? `0 0 0 4px ${accent}1A` : "none",
          }}
        >
          <span
            className={cn(
              "absolute top-0.5 h-5 w-5 rounded-full transition-transform duration-200 shadow-md",
              "bg-white",
              checked ? "translate-x-[22px]" : "translate-x-[2px]"
            )}
            style={{
              border: checked ? "none" : "1px solid hsl(var(--border))",
            }}
          />
        </button>
      </div>
    </div>
  );
}
